/* eslint-disable max-len */
/* eslint-disable no-console */
import React from 'react';
import { IconType } from 'react-icons';
import { FiFileText } from 'react-icons/fi';
import { AiOutlineFileImage, AiOutlineFilePdf, AiOutlineFileWord, AiOutlineFileExcel, AiOutlineFilePpt } from 'react-icons/ai';

interface IconProps {
    size?: number;
    color?: string;
}

/**
 * Processes an image URL to ensure it's displayed correctly, especially for Google Photos links
 * @param imageUrl The original image URL
 * @returns A properly formatted URL for use in the application
 */
export const getProcessedImageUrl = (imageUrl: string | undefined): string =>
{
	if (!imageUrl) return '';

	// Handle Google Photos URLs - return as is without timestamp to prevent flickering
	if (imageUrl.includes('googleusercontent.com'))
	{
		return imageUrl;
	}

	return imageUrl;
};

export const getFileTypeIcon = (fileType?: string): JSX.Element =>
{
	const iconProps: IconProps = { size: 24 };

	if (!fileType) return <FiFileText {...iconProps} />;

	switch (fileType.toLowerCase())
	{
		case 'pdf':
			return <AiOutlineFilePdf {...iconProps} color='#E94235' />;
		case 'doc':
		case 'docx':
			return <AiOutlineFileWord {...iconProps} color='#4285F4' />;
		case 'xls':
		case 'xlsx':
			return <AiOutlineFileExcel {...iconProps} color='#0F9D58' />;
		case 'ppt':
		case 'pptx':
			return <AiOutlineFilePpt {...iconProps} color='#F4B400' />;
		case 'jpg':
		case 'jpeg':
		case 'png':
		case 'gif':
		case 'webp':
			return <AiOutlineFileImage {...iconProps} color='#4285F4' />;
		default:
			return <FiFileText {...iconProps} />;
	}
};

/**
 * Get file type icon URL for use as image source (thumbnail)
 * This function returns URL strings instead of JSX elements
 */
export const getFileTypeIconUrl = (fileType?: string): string =>
{
	// Handle null, undefined, or empty string
	if (!fileType || fileType.trim() === '')
	{
		return '/documents/defaultDocument.png';
	}

	// Normalize file type to lowercase and trim whitespace
	const normalizedFileType = fileType.toLowerCase().trim();

	switch (normalizedFileType)
	{
		case 'pdf':
			return '/documents/pdf.png';
		case 'doc':
			return '/documents/doc.png';
		case 'docx':
			return '/documents/docx.png';
		case 'xls':
			return '/documents/xls.png';
		case 'xlsx':
			return '/documents/xlsx.png';
		case 'ppt':
			return '/documents/ppt.png';
		case 'pptx':
			return '/documents/pptx.png';
		case 'txt':
			return '/documents/txt.png';
		case 'jpg':
		case 'jpeg':
		case 'png':
		case 'gif':
		case 'webp':
			return '/documents/image.png';
		default:
			return '/documents/defaultDocument.png';
	}
};

/**
 * Validate if an icon URL exists and is accessible
 * This can be used for testing and debugging icon issues
 */
export const validateIconUrl = async (iconUrl: string): Promise<boolean> =>
{
	try
	{
		const response = await fetch(iconUrl, { method: 'HEAD' });

		return response.ok;
	}
	catch (error)
	{
		console.error('Icon validation failed for:', iconUrl, error);

		return false;
	}
};

/**
 * Test all file type icons and report which ones are missing
 * Useful for debugging icon issues
 */
export const testAllFileTypeIcons = async (): Promise<{ working: string[]; broken: string[] }> =>
{
	const fileTypes = [ 'pdf', 'doc', 'docx', 'xls', 'xlsx', 'ppt', 'pptx', 'txt', 'jpg', 'png' ];
	const working: string[] = [];
	const broken: string[] = [];

	for (const fileType of fileTypes)
	{
		const iconUrl = getFileTypeIconUrl(fileType);
		const isWorking = await validateIconUrl(iconUrl);

		if (isWorking)
		{
			working.push(fileType);
		}
		else
		{
			broken.push(fileType);
			console.error(`Icon missing for ${fileType}: ${iconUrl}`);
		}
	}

	console.log('Icon test results:', { working, broken });

	return { working, broken };
};

type FileIconProps = {
    fileType?: string;
};

const FileIcon: React.FC<FileIconProps> = ({ fileType }) =>
{
	return <>{getFileTypeIcon(fileType)}</>;
};

export default FileIcon;